import { Children, isValidElement, useState, type HTMLAttributes, type ReactNode } from 'react';
import { usePhone } from '@/lib/responsive';
import { ResponsiveOverlay } from './responsive-overlay';
import { Icons } from './primitives';

/**
 * What a control in the filter bar does to the rows, as the phone Filters
 * button counts it:
 *
 * - `filter`: narrows the rows. Counts while its value is not unset (`''`,
 *   `'all'`, or the `data-filter-default` the caller declares).
 * - `window`: narrows the rows by time, and always has a value. Counts only
 *   when the caller declares `data-filter-default` and the value differs from
 *   it; with no declared default nothing here can tell the default apart, so it
 *   is read as unset rather than counted on every visit.
 * - `arrange`: a sort order or a grouping. It reorders the rows and never
 *   removes one, so it never counts.
 *
 * A caller declares the role with `data-filter-role` on the control. Without
 * one it is read from the control's label: `SORT…` and `GROUP…` arrange,
 * `WINDOW`, `WHEN` and `… WINDOW` are windows, everything else filters. Every
 * selection, arranged or not, is still named in the summary beside the button.
 */
export type FilterRole = 'filter' | 'window' | 'arrange';

/**
 * The props a filter control may carry that name its current selection. Every
 * one is optional: the summary reads whichever the control has (a select's
 * `options`, a picker's `items` + `getKey`) and falls back to the raw value.
 */
interface FilterControlProps {
  value?: unknown;
  label?: unknown;
  options?: readonly { id?: unknown; value?: unknown; label?: unknown }[];
  items?: readonly unknown[];
  getKey?: (item: unknown) => unknown;
  getSearchText?: (item: unknown) => unknown;
  children?: ReactNode;
  'data-filter-role'?: unknown;
  'data-filter-default'?: unknown;
}

interface Selection {
  label: string;
  counts: boolean;
}

function isFilterRole(value: unknown): value is FilterRole {
  return value === 'filter' || value === 'window' || value === 'arrange';
}

function filterRole(props: { label?: unknown; 'data-filter-role'?: unknown }): FilterRole {
  const declared = props['data-filter-role'];
  if (isFilterRole(declared)) return declared;
  const label = typeof props.label === 'string' ? props.label.trim() : '';
  if (/^(sort|group)\b/i.test(label)) return 'arrange';
  if (/^(window|when)$|\bwindow\b/i.test(label)) return 'window';
  return 'filter';
}

function counts(p: FilterControlProps): boolean {
  const role = filterRole(p);
  if (role === 'arrange') return false;
  const unset = p['data-filter-default'];
  if (typeof unset === 'string') return p.value !== unset;
  return role === 'filter';
}

function filterSelections(children: ReactNode): Selection[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement<FilterControlProps>(child)) return [];
    const p = child.props;
    if (p.value != null && p.value !== '' && p.value !== 'all') {
      const selected: readonly unknown[] = Array.isArray(p.value) ? p.value : [p.value];
      const labels = selected.flatMap((value) => {
        const option = p.options?.find((o) => (o.id ?? o.value) === value);
        const item = p.items?.find((i) => p.getKey?.(i) === value);
        const label = option?.label ?? (item && p.getSearchText?.(item)) ?? value;
        return typeof label === 'string' ? [label] : [];
      });
      if (labels.length) {
        const counted = counts(p);
        return labels.map((label) => ({ label, counts: counted }));
      }
    }
    return filterSelections(p.children);
  });
}

/** The screen still owns every filter value. The phone sheet hosts the same controls. */
export function ResponsiveFilters({
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  const selections = filterSelections(children);
  // The summary names the sort and the window too ("Most recently active");
  // the number on the button counts only what removes rows.
  const active = selections.map((s) => s.label).join(' · ') || 'All records';
  const filtered = selections.filter((s) => s.counts).length;
  const close = () => setOpen(false);
  if (!phone)
    return (
      <div {...props} className={className || 'filter-bar'}>
        {children}
      </div>
    );
  return (
    <div className="mobile-filter-bar">
      {/* A TabStrip on the page portals its View picker here so both share the row. */}
      <span className="mobile-filter-bar-slot" />
      <button
        type="button"
        className="btn"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {Icons.filter} Filters{filtered > 0 ? ` · ${filtered}` : ''}
      </button>
      <span className="mobile-filter-summary" title={active}>
        {active}
      </span>
      {/* MobileRecords portals its sort button and count here, as on the canvas. */}
      <span className="mobile-filter-bar-tools" />
      {open && (
        <ResponsiveOverlay label="Filters" kind="picker" onClose={close} className="filter-sheet">
          <div className="drawer-hd">
            <h2>Filters</h2>
            <button className="btn" onClick={close}>
              Done
            </button>
          </div>
          <div {...props} className={`filter-bar mobile-filter-controls ${className}`}>
            {children}
          </div>
        </ResponsiveOverlay>
      )}
    </div>
  );
}
